import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { Search, Filter, X, ChevronDown, Settings, Plus, ArrowLeft } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import { getAccounts, addPurchase, getPurchases, updatePurchase, deletePurchase } from '../services/firestoreService';
import { useNavigate } from 'react-router-dom';
import PortalBackground from '../components/PortalBackground';

const ClientPurchases = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Programs');
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [dateFilter, setDateFilter] = useState('All Time');
  const [accounts, setAccounts] = useState([]);
  const [alaCarteItems, setAlaCarteItems] = useState([]);
  const [formData, setFormData] = useState({
    account: '',
    program: '',
    billingCycle: '',
    startDate: '',
    endDate: '',
    items: [],
    date: '',
    total: ''
  });
  const [selectedItems, setSelectedItems] = useState([]);

  // Define stages for each type
  const stages = {
    Programs: [
      { id: 'active', name: 'Active', count: 0 },
      { id: 'pending', name: 'Pending', count: 0 },
      { id: 'paused', name: 'Paused', count: 0 },
      { id: 'cancelled', name: 'Cancelled', count: 0 }
    ],
    'A La Carte': [
      { id: 'draft', name: 'Draft', count: 0 },
      { id: 'pending', name: 'Pending Payment', count: 0 },
      { id: 'paid', name: 'Paid', count: 0 },
      { id: 'completed', name: 'Completed', count: 0 }
    ]
  };

  const getTableHeaders = () => {
    if (activeTab === 'Programs') {
      return [
        { name: 'Account', sortable: true },
        { name: 'Program', sortable: true },
        { name: 'Total', sortable: true },
        { name: 'Billing Cycle', sortable: true },
        { name: 'Start Date', sortable: true },
        { name: 'End Date', sortable: true },
      ];
    } else {
      return [
        { name: 'Account', sortable: true },
        { name: 'Item', sortable: true },
        { name: 'Total', sortable: true },
        { name: 'Date', sortable: true },
      ];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch purchases using the service function
        const purchasesData = await getPurchases();
        setPurchases(purchasesData);

        // Fetch accounts
        const accountsData = await getAccounts();
        setAccounts(accountsData);

        // Fetch A La Carte items
        const alaCarteSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        const alaCarteData = alaCarteSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAlaCarteItems(alaCarteData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft':
        return 'text-gray-600';
      case 'Pending':
        return 'text-yellow-600';
      case 'Paid':
      case 'Active':
        return 'text-green-600';
      case 'Paused':
        return 'text-blue-600';
      case 'Cancelled':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Active':
        return { text: 'text-green-600', border: 'border-green-500' };
      case 'Pending':
        return { text: 'text-yellow-600', border: 'border-yellow-500' };
      case 'Paused':
        return { text: 'text-blue-600', border: 'border-blue-500' };
      case 'Cancelled':
        return { text: 'text-red-600', border: 'border-red-500' };
      case 'Draft':
        return { text: 'text-gray-600', border: 'border-gray-500' };
      case 'Paid':
        return { text: 'text-green-600', border: 'border-green-500' };
      case 'Completed':
        return { text: 'text-green-600', border: 'border-green-500' };
      default:
        return { text: 'text-gray-600', border: 'border-gray-500' };
    }
  };

  const filteredPurchases = purchases.filter(purchase => {
    const matchesSearch = !searchQuery || 
      purchase.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      purchase.contactName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = purchase.type === activeTab;
    return matchesSearch && matchesType;
  });

  const purchasesByStage = stages[activeTab].reduce((acc, stage) => {
    acc[stage.name] = filteredPurchases.filter(purchase => purchase.status === stage.name);
    return acc;
  }, {});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemSelect = (itemId) => {
    const item = alaCarteItems.find(i => i.id === itemId);
    if (item && !selectedItems.find(selected => selected.id === itemId)) {
      const newSelectedItems = [...selectedItems, item];
      setSelectedItems(newSelectedItems);
      setFormData(prev => ({
        ...prev,
        items: newSelectedItems.map(item => item.id)
      }));
    }
  };

  const handleItemRemove = (itemId) => {
    const newSelectedItems = selectedItems.filter(item => item.id !== itemId);
    setSelectedItems(newSelectedItems);
    setFormData(prev => ({
      ...prev,
      items: newSelectedItems.map(item => item.id)
    }));
  };

  const handleRowClick = (purchase) => {
    setEditingPurchase(purchase);
    setActiveTab(purchase.type);
    
    // Pre-populate form with existing data
    const selectedAccount = accounts.find(account => account.id === purchase.accountId);
    setFormData({
      account: purchase.accountId || '',
      program: purchase.program || '',
      billingCycle: purchase.billingCycle || '',
      startDate: purchase.startDate ? (purchase.startDate instanceof Date ? purchase.startDate.toISOString().split('T')[0] : new Date(purchase.startDate.seconds * 1000).toISOString().split('T')[0]) : '',
      endDate: purchase.endDate ? (purchase.endDate instanceof Date ? purchase.endDate.toISOString().split('T')[0] : new Date(purchase.endDate.seconds * 1000).toISOString().split('T')[0]) : '',
      items: purchase.items || [],
      date: purchase.date ? (purchase.date instanceof Date ? purchase.date.toISOString().split('T')[0] : new Date(purchase.date.seconds * 1000).toISOString().split('T')[0]) : '',
      total: purchase.total?.toString() || ''
    });

    // Pre-populate selected items for A La Carte
    if (purchase.type === 'A La Carte' && purchase.items) {
      const itemsToSelect = alaCarteItems.filter(item => purchase.items.includes(item.id));
      setSelectedItems(itemsToSelect);
    } else {
      setSelectedItems([]);
    }

    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingPurchase(null);
    setFormData({
      account: '',
      program: '',
      billingCycle: '',
      startDate: '',
      endDate: '',
      items: [],
      date: '',
      total: ''
    });
    setSelectedItems([]);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editingPurchase) return;

    try {
      setLoading(true);
      
      // Delete from Firestore
      await deletePurchase(editingPurchase.id);
      
      // Remove from local state
      setPurchases(prev => prev.filter(p => p.id !== editingPurchase.id));
      
      // Close modals and reset
      setShowDeleteConfirm(false);
      handleCloseEditModal();
      
      console.log('Purchase deleted successfully');
    } catch (error) {
      console.error('Error deleting purchase:', error);
      alert('Failed to delete purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate A La Carte items
    if (activeTab === 'A La Carte' && selectedItems.length === 0) {
      alert('Please select at least one item for A La Carte purchase.');
      return;
    }
    
    try {
      setLoading(true);
      
      // Find the selected account details
      const selectedAccount = accounts.find(account => account.id === formData.account);
      if (!selectedAccount) {
        alert('Please select a valid account.');
        return;
      }

      // Prepare purchase data
      const purchaseData = {
        type: activeTab,
        accountId: formData.account,
        company: selectedAccount.company,
        contactName: selectedAccount.contact?.firstName && selectedAccount.contact?.lastName 
          ? `${selectedAccount.contact.firstName} ${selectedAccount.contact.lastName}`
          : selectedAccount.contact?.email || '',
        total: parseFloat(formData.total) || 0,
        status: editingPurchase ? editingPurchase.status : (activeTab === 'Programs' ? 'Pending' : 'Draft')
      };

      // Add type-specific fields
      if (activeTab === 'Programs') {
        purchaseData.program = formData.program;
        purchaseData.billingCycle = formData.billingCycle;
        purchaseData.startDate = formData.startDate;
        purchaseData.endDate = formData.endDate;
      } else if (activeTab === 'A La Carte') {
        purchaseData.items = selectedItems.map(item => item.id);
        purchaseData.item = selectedItems.map(item => item.name).join(', ');
        purchaseData.date = formData.date;
      }

      let updatedPurchase;
      if (editingPurchase) {
        // Update existing purchase
        updatedPurchase = await updatePurchase(editingPurchase.id, purchaseData);
        
        // Update local state
        setPurchases(prev => prev.map(p => p.id === editingPurchase.id ? updatedPurchase : p));
        
        // Close edit modal and reset form
        handleCloseEditModal();
        
        console.log('Purchase updated successfully:', updatedPurchase);
      } else {
        // Add new purchase
        updatedPurchase = await addPurchase(purchaseData);
        
        // Add to local state
        setPurchases(prev => [updatedPurchase, ...prev]);
        
        // Close add modal and reset form
        setShowAddModal(false);
        setFormData({
          account: '',
          program: '',
          billingCycle: '',
          startDate: '',
          endDate: '',
          items: [],
          date: '',
          total: ''
        });
        setSelectedItems([]);
        
        console.log('Purchase added successfully:', updatedPurchase);
      }
    } catch (error) {
      console.error('Error saving purchase:', error);
      alert(`Failed to ${editingPurchase ? 'update' : 'add'} purchase. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col p-6 max-w-full relative z-0 text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate('/crm/reports')}
          className={`p-2 rounded-md transition-colors ${
            darkMode 
              ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-3xl font-bold tracking-tight text-center flex-1">Client Purchases</h1>
        <div className="w-[40px]"></div>
      </div>

      {/* Main Tabs */}
      <div className={`border-b mb-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex justify-center">
          {['Programs', 'A La Carte'].map((tab) => (
            <button
              key={tab}
              className={`py-2 px-4 flex items-center ${
                activeTab === tab ? 'border-b-2 border-red-500 text-red-600' : (darkMode ? 'text-gray-300' : 'text-gray-600')
              }`}
              onClick={() => setActiveTab(tab)}
            >
              <span>{tab}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="my-4 flex items-center gap-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search"
            className={`pl-10 pr-4 py-3 w-full border rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Date Range Filter */}
        <div className="relative">
          <select 
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className={`flex items-center border rounded-xl px-3 py-2 gap-1 text-sm appearance-none pr-8 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm`}
          >
            <option value="All Time">All Time</option>
            <option value="This Year">This Year</option>
            <option value="This Quarter">This Quarter</option>
            <option value="This Month">This Month</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
        </div>

        {/* New Button */}
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>New</span>
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <BBALoader size={32} />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-400">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                {getTableHeaders().map((header) => (
                  <th key={header.name} className={`px-4 py-2 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <div className="flex items-center">
                      <span>{header.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredPurchases.length > 0 ? (
                filteredPurchases.map((purchase) => (
                  <tr 
                    key={purchase.id} 
                    onClick={() => handleRowClick(purchase)}
                    className={`border-b cursor-pointer transition-colors ${darkMode ? 'border-gray-700 hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                  >
                    {/* Account column - same for both tabs */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-pink-200 rounded-full flex items-center justify-center text-pink-600">
                          {purchase.company?.[0] || '?'}
                        </div>
                        <div>
                          <div className="font-medium">{purchase.company || 'Unnamed Company'}</div>
                        </div>
                      </div>
                    </td>
                    
                    {activeTab === 'Programs' ? (
                      <>
                        {/* Program */}
                        <td className="px-4 py-4">{purchase.program || 'N/A'}</td>
                        {/* Total */}
                        <td className="px-4 py-4">${purchase.total?.toLocaleString() || '0'}</td>
                        {/* Billing Cycle */}
                        <td className="px-4 py-4">{purchase.billingCycle || 'N/A'}</td>
                        {/* Start Date */}
                        <td className="px-4 py-4">
                          {purchase.startDate ? (purchase.startDate instanceof Date ? purchase.startDate.toLocaleDateString() : new Date(purchase.startDate.seconds * 1000).toLocaleDateString()) : 'N/A'}
                        </td>
                        {/* End Date */}
                        <td className="px-4 py-4">
                          {purchase.endDate ? (purchase.endDate instanceof Date ? purchase.endDate.toLocaleDateString() : new Date(purchase.endDate.seconds * 1000).toLocaleDateString()) : 'N/A'}
                        </td>
                      </>
                    ) : (
                      <>
                        {/* Item */}
                        <td className="px-4 py-4">{purchase.item || 'N/A'}</td>
                        {/* Total */}
                        <td className="px-4 py-4">${purchase.total?.toLocaleString() || '0'}</td>
                        {/* Date */}
                        <td className="px-4 py-4">
                          {purchase.date ? (purchase.date instanceof Date ? purchase.date.toLocaleDateString() : new Date(purchase.date.seconds * 1000).toLocaleDateString()) : 'N/A'}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={activeTab === 'Programs' ? 6 : 4} className={`px-4 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No purchases found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add New Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`w-full max-w-md mx-4 rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1"></div>
                <h2 className={`text-xl font-semibold text-center flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Add Purchase
                </h2>
                <div className="flex-1 flex justify-end">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className={`p-1 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Account */}
                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Account
                  </label>
                  <select
                    name="account"
                    value={formData.account}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md appearance-none pr-8 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    required
                  >
                    <option value="">Select an account</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.company}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
                </div>

                {activeTab === 'Programs' ? (
                  <>
                    {/* Program */}
                    <div className="relative">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Program
                      </label>
                      <select
                        name="program"
                        value={formData.program}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md appearance-none pr-8 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      >
                        <option value="">Select a program</option>
                        <option value="Lite">Lite</option>
                        <option value="Plus">Plus</option>
                        <option value="Pro">Pro</option>
                        <option value="Other">Other</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
                    </div>

                    {/* Billing Cycle */}
                    <div className="relative">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Billing Cycle
                      </label>
                      <select
                        name="billingCycle"
                        value={formData.billingCycle}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md appearance-none pr-8 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      >
                        <option value="">Select billing cycle</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Annual">Annual</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
                    </div>

                    {/* Start Date */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      />
                    </div>

                    {/* End Date */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        End Date
                      </label>
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Items */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Items
                      </label>
                      
                      {/* Selected Items Display */}
                      {selectedItems.length > 0 && (
                        <div className="mb-2">
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                            Selected Items ({selectedItems.length}):
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedItems.map((item) => (
                              <div
                                key={item.id}
                                className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'}`}
                              >
                                <span>{item.icon}</span>
                                <span>{item.name}</span>
                                <button
                                  type="button"
                                  onClick={() => handleItemRemove(item.id)}
                                  className={`ml-1 hover:bg-red-500 hover:text-white rounded-full p-0.5 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600'}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Item Selection Dropdown */}
                      <div className="relative">
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              handleItemSelect(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-md appearance-none pr-8 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        >
                          <option value="">Add an item</option>
                          {alaCarteItems
                            .filter(item => !selectedItems.find(selected => selected.id === item.id))
                            .map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.icon} {item.name}
                              </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
                      </div>
                      
                      {selectedItems.length === 0 && (
                        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Select at least one item
                        </div>
                      )}
                    </div>

                    {/* Date */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Date
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      />
                    </div>
                  </>
                )}

                {/* Total */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {activeTab === 'Programs' ? 'Monthly Total' : 'Total'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="total"
                    value={formData.total}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="0.00"
                    required
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className={`flex-1 px-4 py-2 border rounded-md ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                      loading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-red-500 hover:bg-red-600'
                    } text-white`}
                  >
                    {loading ? 'Adding...' : 'Add Purchase'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Purchase Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`w-full max-w-md mx-4 rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1"></div>
                <h2 className={`text-xl font-semibold text-center flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Edit Purchase
                </h2>
                <div className="flex-1 flex justify-end">
                  <button
                    onClick={handleCloseEditModal}
                    className={`p-1 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Account */}
                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Account
                  </label>
                  <select
                    name="account"
                    value={formData.account}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md appearance-none pr-8 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    required
                  >
                    <option value="">Select an account</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.company}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
                </div>

                {activeTab === 'Programs' ? (
                  <>
                    {/* Program */}
                    <div className="relative">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Program
                      </label>
                      <select
                        name="program"
                        value={formData.program}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md appearance-none pr-8 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      >
                        <option value="">Select a program</option>
                        <option value="Lite">Lite</option>
                        <option value="Plus">Plus</option>
                        <option value="Pro">Pro</option>
                        <option value="Other">Other</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
                    </div>

                    {/* Billing Cycle */}
                    <div className="relative">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Billing Cycle
                      </label>
                      <select
                        name="billingCycle"
                        value={formData.billingCycle}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md appearance-none pr-8 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      >
                        <option value="">Select billing cycle</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Annual">Annual</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
                    </div>

                    {/* Start Date */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      />
                    </div>

                    {/* End Date */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        End Date
                      </label>
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Items */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Items
                      </label>
                      
                      {/* Selected Items Display */}
                      {selectedItems.length > 0 && (
                        <div className="mb-2">
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                            Selected Items ({selectedItems.length}):
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedItems.map((item) => (
                              <div
                                key={item.id}
                                className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'}`}
                              >
                                <span>{item.icon}</span>
                                <span>{item.name}</span>
                                <button
                                  type="button"
                                  onClick={() => handleItemRemove(item.id)}
                                  className={`ml-1 hover:bg-red-500 hover:text-white rounded-full p-0.5 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600'}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Item Selection Dropdown */}
                      <div className="relative">
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              handleItemSelect(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-md appearance-none pr-8 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        >
                          <option value="">Add an item</option>
                          {alaCarteItems
                            .filter(item => !selectedItems.find(selected => selected.id === item.id))
                            .map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.icon} {item.name}
                              </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
                      </div>
                      
                      {selectedItems.length === 0 && (
                        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Select at least one item
                        </div>
                      )}
                    </div>

                    {/* Date */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Date
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        required
                      />
                    </div>
                  </>
                )}

                {/* Total */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {activeTab === 'Programs' ? 'Monthly Total' : 'Total'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="total"
                    value={formData.total}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="0.00"
                    required
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    disabled={loading}
                    className={`px-4 py-2 border rounded-md transition-colors ${
                      loading 
                        ? 'border-gray-400 text-gray-400 cursor-not-allowed' 
                        : darkMode 
                          ? 'border-red-600 text-red-400 hover:bg-red-900 hover:border-red-500' 
                          : 'border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400'
                    }`}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    className={`flex-1 px-4 py-2 border rounded-md ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                      loading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-green-500 hover:bg-green-600'
                    } text-white`}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`w-full max-w-md mx-4 rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Delete Purchase
                </h2>
                <button
                  onClick={handleDeleteCancel}
                  className={`p-1 rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                  Are you sure you want to delete this purchase?
                </p>
                {editingPurchase && (
                  <div className={`p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <strong>Company:</strong> {editingPurchase.company}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <strong>Type:</strong> {editingPurchase.type}
                    </p>
                    {editingPurchase.type === 'Programs' && (
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <strong>Program:</strong> {editingPurchase.program}
                      </p>
                    )}
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <strong>Total:</strong> ${editingPurchase.total?.toLocaleString() || '0'}
                    </p>
                  </div>
                )}
                <p className={`text-sm mt-2 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={loading}
                  className={`flex-1 px-4 py-2 border rounded-md transition-colors ${
                    loading 
                      ? 'border-gray-400 text-gray-400 cursor-not-allowed' 
                      : darkMode 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={loading}
                  className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-red-500 hover:bg-red-600'
                  } text-white`}
                >
                  {loading ? 'Deleting...' : 'Delete Purchase'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ClientPurchases; 